/**
 * Admin Utility Functions
 * Helper functions for admin dashboard and query management
 */

import { QUERY_STATUS_COLORS } from '../constants/queryStatus';

/**
 * Get Tailwind CSS classes for status badge based on query status
 * @param {string} status - Query status
 * @returns {string} Tailwind CSS classes
 */
export const getStatusColor = (status) => {
  return QUERY_STATUS_COLORS[status] || "bg-gray-100 text-gray-800";
};

/**
 * Format status text from snake_case to Title Case
 * @param {string} status - Query status in snake_case
 * @returns {string} Formatted status text
 * 
 * @example
 * formatStatus('pending_agent') // Returns: 'Pending Agent'
 */
export const formatStatus = (status) => {
  if (!status) return 'N/A';
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Format date to localized short format
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date string
 * 
 * @example
 * formatDate('2024-01-04') // Returns: 'Jan 4, 2024'
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Format date with time to localized long format
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date and time string
 * 
 * @example
 * formatDateTime('2024-01-04T10:30:00') // Returns: 'January 4, 2024, 10:30 AM'
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Format currency to USD
 * @param {number} amount - Amount in dollars
 * @returns {string} Formatted currency string
 * 
 * @example
 * formatCurrency(1500) // Returns: '$1,500'
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

